import React, { useMemo } from 'react';
import { Box, Button, CircularProgress, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { MoneyOff } from '@mui/icons-material';
import { MaterialReactTable } from 'material-react-table';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';

import CustomDialog from '../../../layout/CustomDialog';
import { formatarNumero } from '../../../../utils/masks';
import { retornaDataBr } from '../../../../utils/retornaDataFormatada';

function FormBaixas({
    itens,
    isLoading,
    isSubmitting,
    selectedBaixas,
    setSelectedBaixas,
    openConfirm,
    setOpenConfirm,
    handleConfirmEstorno,
    handleEstornarSelecionadas,
    handleEstornarUma,
    onClose,
}) {
    const columns = useMemo(
        () => [
            {
                id: 'lancamentoBaixa',
                header: 'Baixas do lançamento',
                columns: [
                    {
                        accessorKey: 'Lancamento.Pessoa.nomePrincipal',
                        header: 'Parceiro',
                    },
                    {
                        accessorKey: 'Lancamento.valorOriginal',
                        header: 'Valor Total',
                        muiTableBodyCellProps: { align: 'right' },
                        Cell: ({ row }) => formatarNumero(row.original?.Lancamento?.valorOriginal || 0),
                    },
                    {
                        accessorKey: 'Lancamento.parcelas',
                        header: 'Parcela',
                        muiTableBodyCellProps: { align: 'center' },
                    },
                    {
                        accessorKey: 'Lancamento.valor',
                        header: 'Valor Parcela',
                        muiTableBodyCellProps: { align: 'right' },
                        Cell: ({ row }) => formatarNumero(row.original?.Lancamento?.valor || 0),
                    },
                    {
                        accessorKey: 'Lancamento.descontoValor',
                        header: 'Desconto',
                        muiTableBodyCellProps: { align: 'right' },
                        Cell: ({ row }) => formatarNumero(row.original?.Lancamento?.descontoValor || 0),
                    },
                    {
                        accessorKey: 'Lancamento.acrescimoValor',
                        header: 'Acréscimo',
                        muiTableBodyCellProps: { align: 'right' },
                        Cell: ({ row }) => formatarNumero(row.original?.Lancamento?.acrescimoValor || 0),
                    },
                    {
                        accessorKey: 'Lancamento.dataVencimento',
                        header: 'Vencimento',
                        muiTableBodyCellProps: { align: 'center' },
                        Cell: ({ row }) =>
                            row.original?.Lancamento?.dataVencimento ? retornaDataBr(row.original.Lancamento.dataVencimento) : '',
                    },
                    {
                        accessorKey: 'valor',
                        header: 'Valor Baixado',
                        muiTableBodyCellProps: { align: 'right' },
                        Cell: ({ row }) => formatarNumero(row.original?.valor || 0),
                    },
                    {
                        accessorKey: 'dataBaixa',
                        header: 'Data Baixa',
                        muiTableBodyCellProps: { align: 'center' },
                        Cell: ({ row }) => (row.original?.dataBaixa ? retornaDataBr(row.original.dataBaixa) : ''),
                    },
                    {
                        accessorKey: 'documento',
                        header: 'Documento',
                    },
                    {
                        accessorKey: 'numeroBoleto',
                        header: 'Número Boleto',
                    },
                    {
                        accessorKey: 'nossoNumero',
                        header: 'Nosso Número',
                    },
                    {
                        accessorKey: 'Lancamento.Empresa.nomeFantasia',
                        header: 'Empresa',
                    },
                ],
            },
        ],
        []
    );

    const quantidadeSelecionada = selectedBaixas.length;

    return (
        <>
            {openConfirm && (
                <CustomDialog
                    title="Confirmar estorno"
                    content={
                        quantidadeSelecionada > 1
                            ? `Deseja realmente estornar ${quantidadeSelecionada} baixa(s)?`
                            : 'Deseja realmente estornar esta baixa?'
                    }
                    onClose={() => setOpenConfirm(false)}
                    onConfirm={handleConfirmEstorno}
                />
            )}

            <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
                sx={{ mb: 2 }}
            >
                <Typography variant="body1">
                    <strong>Total de baixas:</strong> {Array.isArray(itens) ? itens.length : 0}
                </Typography>

                <Stack direction="row" spacing={1}>
                    <Button variant="contained" color="secondary" onClick={onClose}>
                        Voltar
                    </Button>

                    <Button
                        variant="contained"
                        color="warning"
                        disabled={!selectedBaixas.length || isSubmitting}
                        onClick={handleEstornarSelecionadas}
                    >
                        Estornar selecionadas
                    </Button>
                </Stack>
            </Stack>

            <Box>
                {isLoading ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : (
                    <MaterialReactTable
                        columns={columns}
                        data={itens}
                        enableRowSelection
                        getRowId={(row) => row.uuid}
                        localization={MRT_Localization_PT_BR}
                        initialState={{
                            showColumnFilters: true,
                            density: 'compact',
                        }}
                        state={{
                            isLoading,
                            rowSelection: selectedBaixas.reduce((acc, uuid) => {
                                acc[uuid] = true;
                                return acc;
                            }, {}),
                        }}
                        onRowSelectionChange={(updater) => {
                            const current = selectedBaixas.reduce((acc, uuid) => {
                                acc[uuid] = true;
                                return acc;
                            }, {});

                            const next = typeof updater === 'function' ? updater(current) : updater;

                            const uuids = Object.keys(next).filter((key) => next[key]);
                            setSelectedBaixas(uuids);
                        }}
                        renderRowActions={({ row }) => (
                            <Tooltip arrow placement="top-start" title="Estornar Baixa">
                                <span>
                                    <IconButton
                                        color="error"
                                        onClick={() => handleEstornarUma(row.original)}
                                        disabled={row.original?.estornado === true || isSubmitting}
                                    >
                                        <MoneyOff />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                        enableRowActions
                        positionActionsColumn="first"
                        positionExpandColumn="first"
                    />
                )}
            </Box>
        </>
    );
}

export default FormBaixas;
